// Admin → Import: the PRODUCTION CHECK OFF workbook onto the app's lines, as a wizard — 1 Upload →
// 2 What was found → 3 Decisions → 4 Run → 5 Result. The server reads and plans the file
// (`POST /api/routing/admin/import/plan`, `planImport`); Step 3's decisions and the run are the
// browser's (`import-decisions.tsx`, `import-run.ts`). The plan lives in this page only (never in the
// database): leaving the tab drops it, so a run in progress, or a result not downloaded, asks first.
// The server keeps the parsed file 30 minutes so a changed tick or cut-off plans again without the
// upload. The first screen lists past runs (Resume plans the file again) and the Reset.

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { DownloadIcon, FileSpreadsheetIcon, Loader2Icon, PauseIcon, PlayIcon } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBlocker, useNavigate } from "@tanstack/react-router";
import {
  IMPORT_KIND_LABELS,
  OUTCOME_LABELS,
  PAGES,
  ROUTING_API,
  conflictGroups,
  cutoffProblem,
  defaultDecisions,
  durationText,
  importCards,
  isShippedTab,
  placeOf,
  resetOffered,
  resumable,
  rowId,
  runPlan,
  SECONDS_PER_WRITE,
} from "@app/shared";
import type {
  ImportCard,
  ImportDecisions,
  ImportList,
  ImportPlan,
  ImportRunRecord,
  ImportRunsPayload,
  RunPlan,
} from "@app/shared";
import { ApiRequestError, Button, DataError, Input, TableSkeleton, apiGet } from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { useCarriers, useRoutingData } from "./data";
import { DecisionsStep } from "./import-decisions";
import { RUN_WIDTH, useImportRun } from "./import-run";
import type { RunState } from "./import-run";
import { notify, useMessage } from "./message";

const STEPS = ["Upload", "What was found", "Decisions", "Run", "Result"] as const;
/** A card's open list shows this many rows; the download has them all. */
const SHOWN_ROWS = 500;
const PLAN_PATH = `${ROUTING_API}/admin/import/plan`;
const LIST_PATH = `${ROUTING_API}/admin/import/list.xlsx`;
const RESULT_PATH = `${ROUTING_API}/admin/import/result.xlsx`;
const RUNS_KEY = ["routing", "admin", "import-runs"] as const;

const today = () => new Intl.DateTimeFormat("en-CA").format(new Date());

/**
 * Asked before the Admin page leaves this tab (another tab, or the navigation): set while a run is
 * in progress, or its result is not downloaded. `admin-page.tsx` asks it before switching tabs.
 */
export const importLeaveGuard: { current: (() => Promise<boolean>) | null } = { current: null };

/**
 * A POST of bytes (octet-stream) through the same credentials as the platform's client, its errors
 * the same typed `ApiRequestError`: the platform's client sends JSON only (P11).
 */
async function postBytes(path: string, body: BodyInit | null): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/octet-stream" },
      body,
    });
  } catch {
    throw new ApiRequestError(0, "data_unavailable", "Can't reach the server — try again.");
  }
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { code?: string; error?: string } | null;
    throw new ApiRequestError(
      res.status,
      (err?.code ?? "data_unavailable") as never,
      err?.error ?? "The server could not answer — try again.",
    );
  }
  return res;
}

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function ImportTab() {
  const message = useMessage();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [planned, setPlanned] = useState<{ token: string; plan: ImportPlan } | null>(null);
  const [decisions, setDecisions] = useState<ImportDecisions | null>(null);
  const [resumeOf, setResumeOf] = useState<ImportRunRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ run: RunPlan; state: RunState } | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const worklist = useRoutingData("worklist");
  const carriers = useCarriers();
  const runner = useImportRun();
  const listValues = useMemo(() => worklist.data?.listValues ?? [], [worklist.data]);
  const running = runner.state.status === "running" || runner.state.status === "paused";

  /** Leaving the tab: asked while the run writes, or while its result is not downloaded. */
  const askLeave = async () => {
    if (running)
      return message.confirm(
        "The import is still running",
        "Leaving stops it after the records in flight. Resume picks it up from the first screen.",
        {
          confirmLabel: "Leave anyway",
        },
      );
    if (result && !downloaded)
      return message.confirm(
        "The result is not downloaded",
        "It is not kept anywhere else: the run's record keeps only its counts.",
        {
          confirmLabel: "Leave without it",
        },
      );
    return true;
  };
  const guarded = running || (!!result && !downloaded);
  useEffect(() => {
    importLeaveGuard.current = guarded ? askLeave : null;
    return () => {
      importLeaveGuard.current = null;
    };
  });
  useBlocker({
    shouldBlockFn: async () => guarded && !(await askLeave()),
    enableBeforeUnload: () => guarded,
    disabled: !guarded,
  });

  /**
   * Plans the file with these ticks and cut-off: by the token of the one the server keeps, or — the
   * first time, or once it is over 30 minutes old (a 400) — uploaded.
   */
  const plan = async (choices: { tabs?: string[]; cutoff?: string }, upload: File | null) => {
    setBusy(true);
    const send = async (bytes: File | null) => {
      const q = new URLSearchParams();
      if (bytes) q.set("name", bytes.name);
      else if (planned) q.set("token", planned.token);
      if (choices.tabs) q.set("tabs", JSON.stringify(choices.tabs));
      q.set("cutoff", choices.cutoff ?? planned?.plan.cutoff ?? today());
      const res = await postBytes(`${PLAN_PATH}?${q.toString()}`, bytes ? await bytes.arrayBuffer() : null);
      return (await res.json()) as { token: string; plan: ImportPlan };
    };
    try {
      const answer = await send(upload).catch((e: unknown) => {
        if (!upload && file && e instanceof ApiRequestError && e.status === 400) return send(file);
        throw e;
      });
      if (resumeOf && answer.plan.file.hash && resumeOf.fileHash && answer.plan.file.hash !== resumeOf.fileHash) {
        const ok = await message.confirm(
          "This is not the file the run read",
          `The run you resume read ${resumeOf.fileName}. This one is different: what is already there is skipped either way.`,
          { confirmLabel: "Use this file anyway" },
        );
        if (!ok) return;
      }
      setPlanned(answer);
      setDecisions(defaultDecisions(answer.plan, { listValues }));
      setStep(1);
    } catch (e) {
      await message.error("Couldn't read the file", errorText(e));
    } finally {
      setBusy(false);
    }
  };

  /** Ticks and the cut-off change: the plan again, the other choice kept (Step 3's decisions start again). */
  const replan = (choices: { tabs?: string[]; cutoff?: string }) =>
    planned && plan({ tabs: planned.plan.tabs.filter((t) => t.ticked).map((t) => t.name), ...choices }, null);

  const runPlanNow = useMemo(
    () =>
      planned && decisions
        ? runPlan(planned.plan, decisions, {
            lines: planned.plan.lines,
            listValues,
            carriers: carriers.data?.options ?? [],
          })
        : null,
    [planned, decisions, listValues, carriers.data],
  );

  const startRun = async () => {
    if (!planned || !decisions || !runPlanNow) return;
    const d = decisions;
    const decided = [
      `${conflictGroups(planned.plan).length} conflicts decided`,
      `${Object.values(d.differences).filter((v) => v === "approve").length} differences kept as the app has them`,
      `${Object.values(d.differences).filter((v) => v === "updated").length} imported as UPDATED`,
    ].join(", ");
    const lines = `${runPlanNow.lines.filter((l) => l.write !== "skip").length} lines, ${runPlanNow.pickups.length} pickups planned`;
    setStep(3);
    try {
      const state = await runner.start(planned.plan, runPlanNow, { lines, decided }, resumeOf);
      setResult({ run: runPlanNow, state });
      setDownloaded(false);
      if (state.failures.length)
        void message.error(
          `${state.failures.length} record${state.failures.length === 1 ? "" : "s"} failed`,
          "The run went on. They are listed in the result and its file; Resume (or a run again) writes what is missing.",
          state.failures.slice(0, 8).map((f) => `${f.what}: ${f.error}`),
        );
      setStep(4);
      await queryClient.invalidateQueries({ queryKey: RUNS_KEY });
    } catch (e) {
      await message.error("The import could not start", errorText(e));
      setStep(2);
    }
  };

  const goStep = (i: number) => {
    if (running || i === step) return;
    if (i === 0 || (i <= 2 && planned) || (i === 4 && result)) setStep(i);
  };

  return (
    <div className="space-y-4" data-testid="admin-import">
      <StepBar
        step={step}
        reachable={(i) => !running && (i === 0 || (i <= 2 && !!planned) || (i === 4 && !!result))}
        onStep={goStep}
      />
      {step === 0 ? (
        <>
          <UploadStep
            file={file}
            busy={busy}
            resumeOf={resumeOf}
            onFile={(f) => {
              setFile(f);
              setPlanned(null);
            }}
            onRead={() => file && void plan({}, file)}
            onCancelResume={() => setResumeOf(null)}
          />
          <RunsAndReset
            onResume={(r) => {
              setResumeOf(r);
              notify(
                "Choose the file again",
                "The plan was never stored: it is made again, and what is already there is skipped.",
              );
            }}
            reset={runner.resetAll}
          />
        </>
      ) : step === 1 && planned ? (
        <FoundStep
          plan={planned.plan}
          busy={busy}
          onReplan={(c) => void replan(c)}
          onBack={() => setStep(0)}
          onNext={() => setStep(2)}
        />
      ) : step === 2 && planned && decisions ? (
        <div className="space-y-3">
          <DecisionsStep
            plan={planned.plan}
            decisions={decisions}
            onChange={setDecisions}
            listValues={listValues}
            carriers={carriers.data?.options ?? []}
          />
          <div className="flex flex-wrap items-center gap-3 border-t pt-3">
            <Button variant="outline" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button disabled={!runner.ready || !runPlanNow} onClick={() => void startRun()} data-testid="import-run">
              {resumeOf ? "Resume the run" : "Run the import"}
            </Button>
            {runPlanNow ? (
              <span className="text-muted-foreground text-sm">
                {runPlanNow.total.toLocaleString("en-US")} records — about{" "}
                {durationText(Math.round((runPlanNow.total * SECONDS_PER_WRITE) / RUN_WIDTH))}
                {!runner.ready ? " · loading the Worklist…" : ""}
              </span>
            ) : null}
          </div>
        </div>
      ) : step === 3 ? (
        <RunStep state={runner.state} onPause={runner.pause} onResume={runner.resume} />
      ) : step === 4 && result && planned ? (
        <ResultStep
          plan={planned.plan}
          result={result}
          decisions={decisions}
          onDownloaded={() => setDownloaded(true)}
          onNew={() => {
            runner.reset();
            setResult(null);
            setPlanned(null);
            setResumeOf(null);
            setStep(0);
          }}
        />
      ) : null}
    </div>
  );
}

function StepBar({
  step,
  reachable,
  onStep,
}: {
  step: number;
  reachable: (i: number) => boolean;
  onStep: (i: number) => void;
}) {
  return (
    <ol className="flex flex-wrap items-center gap-1 text-sm" aria-label="Import steps">
      {STEPS.map((s, i) => (
        <li key={s} className="flex items-center gap-1">
          {i > 0 ? <span className="text-muted-foreground px-1">→</span> : null}
          <button
            type="button"
            disabled={!reachable(i) && i !== step}
            aria-current={i === step ? "step" : undefined}
            onClick={() => onStep(i)}
            className={cn(
              "rounded-full border px-3 py-1",
              i === step ? "border-primary bg-primary text-primary-foreground" : "text-muted-foreground",
              !reachable(i) && i !== step && "cursor-not-allowed opacity-60",
            )}
          >
            {i + 1} {s}
          </button>
        </li>
      ))}
    </ol>
  );
}

function UploadStep({
  file,
  busy,
  resumeOf,
  onFile,
  onRead,
  onCancelResume,
}: {
  file: File | null;
  busy: boolean;
  resumeOf: ImportRunRecord | null;
  onFile: (f: File | null) => void;
  onRead: () => void;
  onCancelResume: () => void;
}) {
  return (
    <div className="max-w-2xl space-y-3 rounded-md border p-4">
      {resumeOf ? (
        <p className="rounded bg-amber-50 p-2 text-sm text-amber-900">
          Resuming the run of {resumeOf.fileName} ({new Date(resumeOf.startedAt).toLocaleString()}): choose the file
          again. It is planned against the data as it is now; every line and pickup already there is skipped.{" "}
          <button type="button" className="underline" onClick={onCancelResume}>
            Start a new run instead
          </button>
        </p>
      ) : null}
      <p className="text-sm">
        Choose the PRODUCTION CHECK OFF workbook (.xlsx). Its tabs are found by their headers; nothing is saved until
        you run the import.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="max-w-sm"
          aria-label="Workbook"
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        />
        <Button disabled={!file || busy} onClick={onRead} className="gap-1">
          {busy ? <Loader2Icon className="size-4 animate-spin" /> : <FileSpreadsheetIcon className="size-4" />}
          {busy ? "Reading…" : "Read the file"}
        </Button>
      </div>
      {file ? (
        <p className="text-muted-foreground text-xs">
          {file.name} · {(file.size / 1e6).toFixed(1)} MB · read against A2000 and Deposco as the Worklist has them now
        </p>
      ) : null}
    </div>
  );
}

const RESET_WORD = "RESET";

/** Past runs (Resume on a Running / Paused one under 30 days old) and the Reset's danger zone. */
function RunsAndReset({
  onResume,
  reset,
}: {
  onResume: (r: ImportRunRecord) => void;
  reset: ReturnType<typeof useImportRun>["resetAll"];
}) {
  const message = useMessage();
  const queryClient = useQueryClient();
  const data = useQuery({
    queryKey: RUNS_KEY,
    queryFn: ({ signal }) => apiGet<ImportRunsPayload>(`${ROUTING_API}/admin/import/runs`, signal),
    staleTime: 10_000,
  });
  const [typed, setTyped] = useState("");
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  if (data.isPending) return <TableSkeleton />;
  if (data.isError) return <DataError error={data.error} />;
  const { runs, appEnv, counts } = data.data;
  const now = new Date();
  const doReset = async () => {
    const ok = await message.confirm(
      "Remove every line, pickup, shipment and pre-route?",
      "Everything the routing pages hold goes, for everyone. The System log stays, and so do the lists, divisions, customer names, routing rules, assignments, saved views and past runs.",
      {
        lines: [
          `${counts.lines} lines and ${counts.pickTickets} pick-ticket copies`,
          `${counts.pickups} pickups, ${counts.shippingRows} shipping rows, ${counts.shipments} shipments`,
          `${counts.preRoutes} pre-routes`,
        ],
        confirmLabel: "Remove them",
      },
    );
    if (!ok) return;
    setProgress({ done: 0, total: 0 });
    try {
      const failed = await reset((done, total) => setProgress({ done, total }));
      if (failed.length)
        await message.error(
          `${failed.length} record${failed.length === 1 ? "" : "s"} could not be removed`,
          "Run the Reset again to remove them.",
          failed.slice(0, 8).map((f) => `${f.what}: ${f.error}`),
        );
      else notify("Reset done", "The System log has one entry saying what was removed.");
    } catch (e) {
      await message.error("The Reset could not start", errorText(e));
    } finally {
      setProgress(null);
      setTyped("");
      await queryClient.invalidateQueries({ queryKey: RUNS_KEY });
    }
  };
  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <h3 className="text-sm font-medium">Past runs</h3>
        {runs.length ? (
          <table className="w-full max-w-5xl text-xs">
            <thead className="bg-locked text-left">
              <tr className="[&>th]:px-2 [&>th]:py-1 [&>th]:font-medium">
                <th>File</th>
                <th>Who</th>
                <th>Started</th>
                <th>Status</th>
                <th>Counts</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-t align-top [&>td]:px-2 [&>td]:py-1">
                  <td>{r.fileName}</td>
                  <td>{r.startedBy}</td>
                  <td>{new Date(r.startedAt).toLocaleString()}</td>
                  <td>{r.status}</td>
                  <td className="max-w-96 break-words" title={r.summary}>
                    {runCountsText(r.counts)}
                  </td>
                  <td>
                    {resumable(r, now) ? (
                      <Button size="xs" variant="outline" onClick={() => onResume(r)}>
                        Resume
                      </Button>
                    ) : r.status === "Running" || r.status === "Paused" ? (
                      <span className="text-muted-foreground">Over 30 days old: can't be resumed</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-muted-foreground text-xs">No run yet.</p>
        )}
      </section>
      {resetOffered(appEnv, counts) ? (
        <section className="max-w-2xl space-y-2 rounded-md border border-red-300 p-4" data-testid="import-reset">
          <h3 className="text-sm font-medium text-red-700">Danger zone</h3>
          <p className="text-xs">
            <b>Reset the routing data</b>: removes every line ({counts.lines}), pick-ticket copy ({counts.pickTickets}),
            pickup ({counts.pickups}), shipping row ({counts.shippingRows}), shipment ({counts.shipments}) and pre-route
            ({counts.preRoutes}), one call each. The System log is never removed.
            {appEnv === "production" ? " On production only while none of them exists." : ""}
          </p>
          <div className="flex items-center gap-2">
            <Input
              className="h-7 w-48 text-xs"
              placeholder={`Type ${RESET_WORD}`}
              value={typed}
              disabled={!!progress}
              onChange={(e) => setTyped(e.target.value)}
              aria-label={`Type ${RESET_WORD} to reset`}
            />
            <Button
              size="xs"
              variant="destructive"
              disabled={typed !== RESET_WORD || !!progress}
              onClick={() => void doReset()}
            >
              Reset the routing data
            </Button>
            {progress ? (
              <span className="text-muted-foreground text-xs tabular-nums">
                Removing {progress.done.toLocaleString("en-US")} of {progress.total.toLocaleString("en-US")}…
              </span>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}

/** A run's counts (its JSON) in words. */
function runCountsText(counts: string) {
  try {
    const c = JSON.parse(counts) as { planned?: Record<string, number>; written?: number; failed?: number };
    const p = c.planned ?? {};
    return `${c.written ?? 0} written, ${c.failed ?? 0} failed · planned ${p.lines ?? 0} lines, ${p.pickups ?? 0} pickups, ${p.shippingRows ?? 0} rows, ${p.values ?? 0} list values`;
  } catch {
    return counts;
  }
}

function RunStep({ state, onPause, onResume }: { state: RunState; onPause: () => void; onResume: () => void }) {
  const pct = state.total ? Math.round((state.done / state.total) * 100) : 0;
  return (
    <div className="max-w-3xl space-y-3 rounded-md border p-4" data-testid="import-progress">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium">
          {state.status === "paused" ? "Paused" : "Writing"} {state.done.toLocaleString("en-US")} of{" "}
          {state.total.toLocaleString("en-US")} records
        </span>
        {state.status === "paused" ? (
          <Button size="xs" className="ml-auto gap-1" onClick={onResume}>
            <PlayIcon className="size-3.5" /> Go on
          </Button>
        ) : (
          <Button size="xs" variant="outline" className="ml-auto gap-1" onClick={onPause}>
            <PauseIcon className="size-3.5" /> Pause
          </Button>
        )}
      </div>
      <div className="bg-muted h-2 overflow-hidden rounded">
        <div className="bg-primary h-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-muted-foreground truncate text-xs">{state.current || "Starting…"}</p>
      {state.failures.length ? (
        <div className="rounded border border-red-300 p-2 text-xs text-red-800">
          <b>{state.failures.length} failed</b> (the run goes on):
          <ul className="mt-1 max-h-40 overflow-auto">
            {state.failures.map((f, i) => (
              <li key={i}>
                {f.what}: {f.error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ResultStep({
  plan,
  result,
  decisions,
  onDownloaded,
  onNew,
}: {
  plan: ImportPlan;
  result: { run: RunPlan; state: RunState };
  decisions: ImportDecisions | null;
  onDownloaded: () => void;
  onNew: () => void;
}) {
  const message = useMessage();
  const navigate = useNavigate();
  const { run, state } = result;
  const byTab = useMemo(() => {
    const m = new Map<string, Map<string, number>>();
    for (const r of plan.rows) {
      const tab = r.tab.trim();
      const outcome = run.rows[rowId(r)]?.outcome ?? OUTCOME_LABELS[r.outcome];
      const t = m.get(tab) ?? new Map<string, number>();
      t.set(outcome, (t.get(outcome) ?? 0) + 1);
      m.set(tab, t);
    }
    return m;
  }, [plan, run]);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      await downloadFile(
        RESULT_PATH,
        resultFile(plan, result, decisions),
        `Import result ${plan.file.name.replace(/\.xlsx$/i, "")}`,
      );
      onDownloaded();
    } catch (e) {
      await message.error("Couldn't make the file", errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const created = Object.keys(state.ids.lines).length;
  return (
    <div className="space-y-4" data-testid="import-result">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm">
          {created.toLocaleString("en-US")} lines, {Object.keys(state.ids.pickups).length.toLocaleString("en-US")}{" "}
          pickups, {Object.keys(state.ids.values).length.toLocaleString("en-US")} list values ·{" "}
          <b className={state.failures.length ? "text-red-700" : undefined}>{state.failures.length} failed</b>
        </span>
        <Button
          size="xs"
          variant="outline"
          className="ml-auto"
          onClick={() => void navigate({ to: PAGES.worklist.path as never, search: { "v.imp": "1" } as never })}
        >
          Open the Worklist on the imported lines
        </Button>
        <Button size="xs" className="gap-1" disabled={busy} onClick={() => void download()}>
          <DownloadIcon className="size-3.5" /> {busy ? "Making the file…" : "Download the result .xlsx"}
        </Button>
        <Button size="xs" variant="outline" onClick={onNew}>
          New import
        </Button>
      </div>
      <table className="text-xs">
        <thead className="bg-locked text-left">
          <tr className="[&>th]:px-2 [&>th]:py-1 [&>th]:font-medium">
            <th>Tab</th>
            <th>Outcome</th>
            <th className="text-right">Rows</th>
          </tr>
        </thead>
        <tbody>
          {[...byTab].flatMap(([tab, m]) =>
            [...m].map(([outcome, n]) => (
              <tr key={`${tab}|${outcome}`} className="border-t [&>td]:px-2 [&>td]:py-0.5">
                <td>{tab}</td>
                <td>{outcome}</td>
                <td className="text-right tabular-nums">{n.toLocaleString("en-US")}</td>
              </tr>
            )),
          )}
        </tbody>
      </table>
      {state.failures.length ? (
        <div className="rounded border border-red-300 p-2 text-xs text-red-800">
          <b>Failed</b>
          <ul className="mt-1 max-h-60 overflow-auto">
            {state.failures.map((f, i) => (
              <li key={i}>
                {f.what}: {f.error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** The result file: each row's final outcome, the lines and pickups with their records, the failures. */
function resultFile(
  plan: ImportPlan,
  { run, state }: { run: RunPlan; state: RunState },
  decisions: ImportDecisions | null,
): { title: string; lists: ImportList[] } {
  const ids = state.ids;
  return {
    title: `Import result ${plan.file.name}`,
    lists: [
      {
        title: "Rows",
        columns: [
          { key: "tab", label: "Tab" },
          { key: "n", label: "Excel row" },
          { key: "customer", label: "Customer (file)" },
          { key: "cell", label: "PO / PT (file)" },
          { key: "planned", label: "Found" },
          { key: "outcome", label: "Outcome" },
          { key: "lines", label: "Lines" },
          { key: "ids", label: "Records" },
          { key: "diffs", label: "Differs from the source" },
          { key: "decision", label: "Differences" },
          { key: "note", label: "Note" },
        ],
        rows: plan.rows.map((r) => {
          const res = run.rows[rowId(r)];
          const lines = res?.lines ?? [];
          return {
            tab: r.tab.trim(),
            n: r.n,
            customer: r.customer,
            cell: r.cell,
            planned: OUTCOME_LABELS[r.outcome],
            outcome: res?.outcome ?? "",
            lines: lines.map(placeOf).join(", "),
            ids: lines
              .map((k) => ids.lines[k] ?? "")
              .filter(Boolean)
              .join(", "),
            diffs: r.diffs.map((x) => `${x.field} ${x.file} → ${x.app}`).join("; "),
            decision: r.diffs.length
              ? decisions?.differences[rowId(r)] === "updated"
                ? "UPDATED"
                : "Approved as the app has it"
              : "",
            note: res?.note ?? "",
          };
        }),
      },
      {
        title: "Lines",
        columns: [
          { key: "place", label: "Line" },
          { key: "source", label: "Source" },
          { key: "write", label: "Write" },
          { key: "id", label: "Record" },
          { key: "rows", label: "Rows" },
          { key: "notes", label: "Notes" },
        ],
        rows: run.lines.map((l) => ({
          place: l.place,
          source: l.source,
          write: l.write === "create" ? "New, Approved" : l.write === "update" ? "Updated" : "Skipped",
          id: ids.lines[l.key] ?? l.id ?? "",
          rows: l.rows.join(", "),
          notes: l.notes.join("; "),
        })),
      },
      {
        title: "Pickups",
        columns: [
          { key: "entry", label: "Entry" },
          { key: "day", label: "Day" },
          { key: "loadRef", label: "PICK UP # / BOL" },
          { key: "carrier", label: "Carrier" },
          { key: "id", label: "Record" },
          { key: "lines", label: "Lines" },
        ],
        rows: [
          ...run.pickups.map((p) => ({
            entry: p.entry,
            day: p.day,
            loadRef: p.input.loadRef,
            carrier: p.input.carrier,
            id: ids.pickups[p.entry] ?? "",
            lines: p.rows.map((r) => placeOf(r.line)).join(", "),
          })),
          ...Object.entries(run.entries)
            .filter(([, e]) => e.outcome !== "Pickup")
            .map(([entry, e]) => ({
              entry,
              day: "",
              loadRef: "",
              carrier: e.outcome,
              id: "",
              lines: e.lines.map(placeOf).join(", "),
            })),
        ],
      },
      {
        title: "Failures",
        columns: [
          { key: "what", label: "Record" },
          { key: "error", label: "Error" },
        ],
        rows: state.failures.map((f) => ({ ...f })),
      },
    ],
  };
}

/** A file made by the server from gzipped JSON (octet-stream, P11), saved by the browser. */
async function downloadFile(path: string, body: unknown, name: string) {
  const gz = new Blob([JSON.stringify(body)]).stream().pipeThrough(new CompressionStream("gzip"));
  const res = await postBytes(path, await new Response(gz).arrayBuffer());
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}.xlsx`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function FoundStep({
  plan,
  busy,
  onReplan,
  onBack,
  onNext,
}: {
  plan: ImportPlan;
  busy: boolean;
  onReplan: (c: { tabs?: string[]; cutoff?: string }) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const cards = useMemo(() => importCards(plan), [plan]);
  const loaded = [
    plan.loadedAt.a2000 ? `A2000 loaded ${new Date(plan.loadedAt.a2000).toLocaleString()}` : "",
    plan.loadedAt.deposco ? `Deposco ${new Date(plan.loadedAt.deposco).toLocaleString()}` : "",
  ].filter(Boolean);
  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-xs">
        {plan.file.name} · planned {new Date(plan.plannedAt).toLocaleString()}
        {loaded.length ? ` · ${loaded.join(" · ")}` : ""}
        {busy ? " · planning again…" : ""}
      </p>
      <div className={cn("grid gap-3 lg:grid-cols-2", busy && "opacity-60")}>
        {cards.map((c) => (
          <CardBox key={c.id} card={c}>
            {c.id === "tabs" ? <TabTicks plan={plan} busy={busy} onReplan={onReplan} /> : null}
            {c.id === "schedule" ? (
              <CutoffBox value={plan.cutoff} busy={busy} onSet={(cutoff) => onReplan({ cutoff })} />
            ) : null}
          </CardBox>
        ))}
      </div>
      <div className="flex items-center gap-3 border-t pt-3">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onNext} disabled={busy} data-testid="import-next">
          Next: decisions
        </Button>
      </div>
    </div>
  );
}

/**
 * The schedule's cut-off: planned again only on a complete date, on Enter or leaving the box — never
 * on each keystroke (a year being typed is 0001 for a moment). A year before 2020 is refused.
 */
function CutoffBox({ value, busy, onSet }: { value: string; busy: boolean; onSet: (day: string) => void }) {
  const message = useMessage();
  const [typed, setTyped] = useState(value);
  const last = useRef(value);
  useEffect(() => {
    setTyped(value);
    last.current = value;
  }, [value]);
  const commit = async () => {
    if (typed === last.current) return;
    const problem = cutoffProblem(typed);
    if (problem) {
      await message.error("Check the cut-off", problem);
      setTyped(last.current);
      return;
    }
    last.current = typed;
    onSet(typed);
  };
  return (
    <label className="flex items-center gap-2 text-xs">
      Cut-off
      <Input
        type="date"
        className="h-7 w-40 text-xs"
        value={typed}
        disabled={busy}
        onChange={(e) => setTyped(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void commit();
          }
        }}
      />
      <span className="text-muted-foreground">
        entries under day sections on or after it · Enter or leave the box to plan again
      </span>
    </label>
  );
}

function TabTicks({
  plan,
  busy,
  onReplan,
}: {
  plan: ImportPlan;
  busy: boolean;
  onReplan: (c: { tabs: string[] }) => void;
}) {
  const ticked = plan.tabs.filter((t) => t.ticked).map((t) => t.name);
  return (
    <ul className="space-y-1 text-xs">
      {plan.tabs
        .filter((t) => t.kind)
        .map((t) => (
          <li key={t.name}>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={t.ticked}
                disabled={busy || isShippedTab(t)}
                onChange={(e) =>
                  onReplan({ tabs: e.target.checked ? [...ticked, t.name] : ticked.filter((x) => x !== t.name) })
                }
              />
              <span className="font-medium">{t.name.trim()}</span>
              <span className="text-muted-foreground">
                {IMPORT_KIND_LABELS[t.kind!]} · {t.rowCount.toLocaleString("en-US")} rows{t.note ? ` · ${t.note}` : ""}
              </span>
            </label>
          </li>
        ))}
    </ul>
  );
}

function CardBox({ card, children }: { card: ImportCard; children?: ReactNode }) {
  const message = useMessage();
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      await downloadList(card.list);
    } catch (e) {
      await message.error("Couldn't make the file", errorText(e));
    } finally {
      setDownloading(false);
    }
  };
  return (
    <section className="space-y-2 rounded-md border p-3" data-testid={`import-card-${card.id}`}>
      <header className="flex items-baseline gap-2">
        <h3 className="text-sm font-medium">{card.title}</h3>
        <span className="ml-auto text-lg font-semibold tabular-nums">{card.count.toLocaleString("en-US")}</span>
      </header>
      <ul className="text-muted-foreground space-y-0.5 text-xs">
        {card.lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      {children}
      <div className="flex gap-2">
        <Button size="xs" variant="outline" onClick={() => setOpen((o) => !o)} disabled={!card.list.rows.length}>
          {open ? "Close list" : "Open list"}
        </Button>
        <Button
          size="xs"
          variant="outline"
          className="gap-1"
          disabled={!card.list.rows.length || downloading}
          onClick={() => void download()}
        >
          <DownloadIcon className="size-3.5" />
          {downloading ? "Making the file…" : "Download .xlsx"}
        </Button>
      </div>
      {open ? <ListTable list={card.list} /> : null}
    </section>
  );
}

/** A card's list: sortable by any column (a click; again, the other way), Excel row first. */
function ListTable({ list }: { list: ImportList }) {
  const [sort, setSort] = useState<{ key: string; desc: boolean } | null>(null);
  const rows = useMemo(() => {
    if (!sort) return list.rows;
    const v = (r: Record<string, string | number>) => r[sort.key] ?? "";
    return [...list.rows].sort((a, b) => {
      const x = v(a);
      const y = v(b);
      const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
      return sort.desc ? -c : c;
    });
  }, [list, sort]);
  return (
    <div className="max-h-96 overflow-auto rounded border">
      <table className="w-full text-[12px]">
        <thead className="bg-locked sticky top-0 text-left">
          <tr>
            {list.columns.map((c) => (
              <th key={c.key} className="px-2 py-1 font-medium whitespace-nowrap">
                <button
                  type="button"
                  onClick={() => setSort((s) => ({ key: c.key, desc: s?.key === c.key ? !s.desc : false }))}
                >
                  {c.label}
                  {sort?.key === c.key ? (sort.desc ? " ↓" : " ↑") : ""}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, SHOWN_ROWS).map((r, i) => (
            <tr key={i} className="border-t align-top">
              {list.columns.map((c) => (
                <td key={c.key} className="max-w-72 px-2 py-1 break-words">
                  {typeof r[c.key] === "number"
                    ? (r[c.key] as number).toLocaleString("en-US", { useGrouping: c.key !== "n" })
                    : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > SHOWN_ROWS ? (
        <p className="text-muted-foreground p-2 text-xs">
          Showing {SHOWN_ROWS.toLocaleString("en-US")} of {rows.length.toLocaleString("en-US")} — the download has them
          all.
        </p>
      ) : null}
    </div>
  );
}

/** The list as an .xlsx: sent gzipped (octet-stream, P11), written by the server, saved by the browser. */
const downloadList = (list: ImportList) => downloadFile(LIST_PATH, list, list.title);
